import React, { useMemo, useState, useCallback, useEffect } from 'react';
import cn from 'classnames';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Subtitle from 'components/UI/Subtitle';
import Link from 'components/UI/Link';
import Slider from 'components/UI/Slider';
import { CustomDot, CustomPagination } from 'components/UI/Slider/utils';
import LazyImage from 'components/UI/LazyImage';
import Tags from 'components/UI/Tags';
import Markup from 'components/Markup';
import { withErrorBoundary } from 'components/ErrorBoundary';
import * as styles from './TechnologiesSlider.module.scss';

const ToolWrapper = ({ children, className, link, onAnimationEnd }) => {
  return link ? (
    <Link href={link} className={className} onAnimationEnd={onAnimationEnd}>
      {children}
    </Link>
  ) : (
    <div className={className} onAnimationEnd={onAnimationEnd}>
      {children}
    </div>
  );
};

function TechnologiesSlider({
  title,
  description,
  descriptionMarkup,
  tools,
  main = false,
  footer,
  filters,
}) {
  const [current, setCurrent] = useState(0);
  const [activeFilter, setActiveFilter] = useState('');
  const [fadeInAnimation, setFadeInAnimation] = useState(false);
  const [sliderReinitTrigger, setSliderReinitTrigger] = useState(null);

  useEffect(() => {
    filters && setActiveFilter(filters[0].type);
  }, [filters]);

  const setCurrentKey = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const filterTech = useCallback(type => {
    setActiveFilter(type);
    setSliderReinitTrigger(type);
    setFadeInAnimation(true);
  }, []);

  const onAnimationEnd = () => {
    setFadeInAnimation(false);
  };

  const filteredTech = useMemo(() => {
    const arr = [];

    filters &&
      tools.forEach(item => {
        const tech = item?.spec?.find(el => el.type === activeFilter);
        tech && Object.keys(tech).length && arr.push({ ...item, orderID: tech.orderID || null });
      });

    return filters ? arr.sort((a, b) => a.orderID - b.orderID) : tools;
  }, [activeFilter, tools]);

  const settings = useMemo(
    () => ({
      customPaging: CustomDot.bind(null, filteredTech.length, current / 5),
      appendDots: CustomPagination,
      dotsClass: 'slick-pagination',
      dots: true,
      slidesToShow: filteredTech.length > 4 ? 5 : filteredTech.length,
      slidesToScroll: 5,
      arrows: true,
      infinite: true,
      variableWidth: filteredTech.length < 5,
      responsive: [
        {
          breakpoint: 961,
          settings: {
            arrows: false,
          },
        },
        {
          breakpoint: 885,
          settings: {
            customPaging: CustomDot.bind(null, filteredTech.length, current / 3),
            slidesToShow: filteredTech.length > 3 ? 3 : filteredTech.length,
            slidesToScroll: 3,
            variableWidth: filteredTech.length < 3,
            arrows: false,
          },
        },
        {
          breakpoint: 500,
          settings: {
            customPaging: CustomDot.bind(null, filteredTech.length, current / 2),
            slidesToShow: filteredTech.length > 2 ? 2 : filteredTech.length,
            slidesToScroll: 2,
            variableWidth: filteredTech.length < 2,
            arrows: false,
          },
        },
      ],
    }),
    [filteredTech.length, current],
  );

  return (
    <Container id="technologies" className="defaultSlider">
      <div className={styles.wrapper}>
        <Title tag="h2">{title}</Title>
        {description && <Subtitle size="lg">{description}</Subtitle>}
        {descriptionMarkup && (
          <Markup tag="p" className={styles.description}>
            {descriptionMarkup}
          </Markup>
        )}
        {filters?.length && <Tags tags={filters} active={activeFilter} handleClick={filterTech} />}
        <div
          className={cn(
            styles.cardsWrapper,
            !filters && styles.smallerMinHeight,
            fadeInAnimation && styles.fadeInAnimation,
          )}
          key={sliderReinitTrigger}
        >
          <Slider {...settings} beforeChange={setCurrentKey}>
            {filteredTech.map(item => {
              return (
                <ToolWrapper
                  key={item.title}
                  className={cn(
                    styles.itemWrapper,
                    filters && styles.itemWrapperWithFilters,
                    (filters || item.link) && styles.itemLink,
                  )}
                  onAnimationEnd={onAnimationEnd}
                  {...item}
                >
                  <LazyImage
                    className={cn(styles.itemIcon, item.withFilter && styles.withFilter)}
                    src={item.icon}
                    alt={item.title}
                  />
                  <h3 className={cn(styles.itemText, item.link && styles.link)}>{item.title}</h3>
                </ToolWrapper>
              );
            })}
          </Slider>
        </div>
        {main && footer && (
          <div className={styles.buttonRow}>
            <Link className={styles.arrowRight} href={footer.link.to}>
              {footer.link.name}
            </Link>
          </div>
        )}
      </div>
    </Container>
  );
}

TechnologiesSlider.displayName = 'TechnologiesSlider';

export default withErrorBoundary(TechnologiesSlider);
